import { inject, Injectable } from "@angular/core";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { BehaviorSubject, map, Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ClassJaxService } from "../../core/services/classjax.service";
import { SupabaseService } from "../../subscription/services/supabase/supabase.service";
import {
  CreditBundle,
  CreditType,
  CREDIT_CONFIG,
  getCreditBundles,
} from "./credits.config";

// Re-export types from config
export { CreditBundle, CreditType } from "./credits.config";

export interface CreditBalance {
  credit_type: CreditType;
  balance: number;
  updated_at: string;
}

export interface CreditTransaction {
  id: string;
  credit_type: CreditType;
  transaction_type: "purchase" | "debit" | "refund" | "bonus" | "adjustment";
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

interface CreditsApiResponse {
  success: boolean;
  data: CreditBalance[];
}

interface CreditBalanceApiResponse {
  success: boolean;
  data: { credit_type: CreditType; balance: number };
}

interface TransactionsApiResponse {
  success: boolean;
  data: CreditTransaction[];
}

@Injectable({
  providedIn: "root",
})
export class CreditsService {
  private api = inject(ClassJaxService);
  private supabase = inject(SupabaseService);

  private _balances$ = new BehaviorSubject<Record<CreditType, number>>({
    image: 0,
  });
  balances$ = this._balances$.asObservable();

  /**
   * Get available credit bundles from config
   */
  getBundles(creditType: CreditType): CreditBundle[] {
    return getCreditBundles(creditType);
  }

  /**
   * Get credit type config (label, description, bundles)
   */
  getCreditTypeConfig(creditType: CreditType) {
    return CREDIT_CONFIG[creditType];
  }

  /**
   * Get all credit balances for the current user
   */
  getBalances(): Observable<CreditBalance[]> {
    return this.api.get<CreditsApiResponse>("/my/credits").pipe(
      map((response) => {
        const balances = response.data || [];
        // Update the BehaviorSubject
        const balanceMap: Record<CreditType, number> = { image: 0 };
        for (const b of balances) {
          balanceMap[b.credit_type] = b.balance;
        }
        this._balances$.next(balanceMap);
        return balances;
      }),
    );
  }

  /**
   * Get balance for a specific credit type
   */
  getBalance(creditType: CreditType): Observable<number> {
    return this.api
      .get<CreditBalanceApiResponse>(`/my/credits/${creditType}`)
      .pipe(
        map((response) => {
          const balance = response.data?.balance ?? 0;
          // Update the specific balance in BehaviorSubject
          const current = this._balances$.value;
          this._balances$.next({ ...current, [creditType]: balance });
          return balance;
        }),
      );
  }

  /**
   * Get credit transaction history
   */
  getTransactions(
    creditType?: CreditType,
    limit = 50,
  ): Observable<CreditTransaction[]> {
    const params: Record<string, string> = { limit: limit.toString() };
    if (creditType) {
      params["credit_type"] = creditType;
    }

    return this.api
      .get<TransactionsApiResponse>("/my/credits/transactions/list", params)
      .pipe(map((response) => response.data || []));
  }

  /**
   * Purchase credits by redirecting to Stripe Checkout
   * Note: This still uses the edge function directly since it creates a Stripe session
   */
  purchaseCredits(
    bundle: CreditBundle,
    creditType: CreditType,
  ): Observable<string> {
    return this.supabase
      .invokeFunction<{ sessionId: string }>("stripe-checkout", {
        creditPurchase: {
          priceId: bundle.priceId,
          credits: bundle.credits,
          creditType: creditType,
          redirectPath: `${location.origin}/usage`,
        },
      })
      .pipe(
        map((res) => {
          if (!res?.sessionId) {
            throw new Error("Failed to create checkout session");
          }
          this._redirectToCheckout(res.sessionId);
          return res.sessionId;
        }),
      );
  }

  /**
   * Check if user has sufficient credits (synchronous check using cached value)
   */
  hasCredits(creditType: CreditType, required: number): boolean {
    return this._balances$.value[creditType] >= required;
  }

  /**
   * Refresh balances from the server
   */
  refreshBalances(): void {
    this.getBalances().subscribe();
  }

  private async _redirectToCheckout(sessionId: string) {
    const stripe = (await loadStripe(
      environment.STRIPE_PUBLISHABLE_KEY,
    )) as Stripe;
    return stripe.redirectToCheckout({ sessionId });
  }
}
